import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useParams } from "react-router-dom";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import {
  getRequestAudit,
  getRequestIdentityVerification,
  getSignRequestData,
  verifyRequestAudit,
} from "../MySignRequestService";
import { SignerIdvSummary } from "types/identityVerification";
import { anySignerRequiresIdv } from "helpers/identityVerificationHelpers";
import {
  AuditChainVerification,
  RequestAudit,
  RequestParticipant,
  SignRequest,
} from "models/interfaces";
// import { Folder } from "global/components/Folders/types";
import ViewSignRequestHeaderSection from "./ViewSignRequestHeaderSection";
import ViewSignRequestSkeleton from "./ViewSignRequestSkeleton";
import AuditTrailCard from "./AuditTimeline";
import RequestParticipants from "./RequestParticipants";
import SignRequestBanner from "./SignRequestBanner";
import AttachmentsCard from "./AttachmentsCard";
import CollectedDataCard from "./CollectedDataCard";
import DocumentsCard from "./DocumentsCard";
import MessageCard from "./MessageCard";
// import RequestDetailsCard from "./RequestDetailsCard";
import strings from "global/constants/StringConstants";
import { GetSaveTemplatePopups } from "screens/Shared/CommonHelper";
import { AppModal } from "global/components/AppModal";
import PostSignReview from "./PostSignReview";
import ViewDocuments from "./ViewDocuments";
import { hasAccessTo, hasWorkspacePermission } from "layouts/Config";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { fetchFolderTree } from "screens/Templates/MyTemplatesService";
// import { fetchSignFolderTree } from "../SignRequestFoldersService";
import { setLoading } from "redux/appSlice";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import {
  buildDeclineReasons,
  buildRecipientTimestamps,
  getCompletedOn,
} from "./ViewRequestStatusMeta";
import viewSignRequestStyles from "./ViewSignRequest.styles";

const styles = viewSignRequestStyles;

interface Audits {
  [key: string]: {
    [key: string]: RequestAudit[];
  };
}

/** Depth-first lookup of a folder's display name by its numeric id. */
// const findFolderName = (tree: Folder[], folderId?: number): string => {
//   if (!folderId) return "";
//   for (const folder of tree ?? []) {
//     if (folder.folderId === folderId) return folder.name;
//     const nested = findFolderName(folder.subFolders ?? [], folderId);
//     if (nested) return nested;
//   }
//   return "";
// };

const ViewSignRequest = () => {
  useTitle(title.SIGN_REQUEST);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { errors, loading, isRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const auditState = useFeatureState(FeatureKeys.COMPLIANCE_AUDIT_TRAIL);
  const isAuditNotInPlan =
    auditState.status === strings.FEATURE_STATUS_NOT_IN_PLAN;
  const isAuditAdminDisabled =
    auditState.status === strings.FEATURE_STATUS_ADMIN_DISABLED;

  const [requestAuditMap, setRequestAuditMap] = useState<Audits>({});
  const [requestAudits, setRequestAudits] = useState<RequestAudit[]>([]);
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  // The audit is fetched on its own call, which resolves after the main request.
  // Tracked separately so the Audit Trail card shows its skeleton until the
  // audit actually arrives, instead of flashing "No activity recorded yet."
  const [isAuditLoading, setIsAuditLoading] = useState(true);
  const [signRequest, setSignRequest] = useState<SignRequest | null>(null);
  const [identityVerification, setIdentityVerification] = useState<
    SignerIdvSummary[]
  >([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<
    RequestParticipant | undefined
  >();
  const [showFullAuditBtn, setShowFullAuditBtn] = useState<boolean>(false);
  const [verification, setVerification] = useState<AuditChainVerification>();
  const [isVerifying, setIsVerifying] = useState(false);
  const [saveTemplatePopup, setSaveTemplatePopup] = useState(false);
  const [selectedFolderData, setSelectedFolderData] = useState<any>(null);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  // const [signFolderName, setSignFolderName] = useState<string>("");
  const [destFolder, setDestFolder] = useState<any>();
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState<boolean>(false);
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] =
    useState<boolean>(false);
  const [documentsPreviewOpen, setDocumentsPreviewOpen] =
    useState<boolean>(false);
  const [documentsPreviewIndex, setDocumentsPreviewIndex] = useState<number>(0);
  // Signed requests open the full-screen review (signed PDF + Chat + Summary).
  const [postSignReviewOpen, setPostSignReviewOpen] = useState<boolean>(false);
  const isSigned =
    signRequest?.status === strings.signed ||
    signRequest?.status === strings.completed;
  // Once signed, an account that merges signed files into one certificated PDF
  // makes the backend serve that SAME merged document for Preview and Download
  // of every file (DocumentSignService.getSignedPdf). Listing one row per upload
  // therefore showed N rows that all opened identical content.
  const showMergedDocumentOnly =
    isSigned &&
    Boolean(signRequest?.signRequestConfig?.attachSingleCertificate);

  useEffect(() => {
    if (isTruthy(id)) {
      fetchData();
    }
  }, [id]);

  const fetchData = () => {
    fetchSignRequestDetails();
    fetchRequestAudit();
  };

  const fetchFolderTreeData = useCallback(async () => {
    try {
      const foldersTree = await fetchFolderTree();
      setFolderTree(foldersTree);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  }, []);

  useEffect(() => {
    fetchFolderTreeData();
  }, [fetchFolderTreeData]);

  // Resolves the "Folder" row in Request details; a failure simply leaves it
  // blank rather than blocking the page. Parked with the card below.
  // useEffect(() => {
  //   const resolveSignFolder = async () => {
  //     if (!signRequest?.parentId) return;
  //     try {
  //       const tree = await fetchSignFolderTree();
  //       setSignFolderName(findFolderName(tree, signRequest.parentId));
  //     } catch {
  //       setSignFolderName("");
  //     }
  //   };
  //   resolveSignFolder();
  // }, [signRequest?.parentId]);

  const fetchSignRequestDetails = async () => {
    try {
      setIsLoading(true);
      const data = await getSignRequestData(id);
      setSignRequest(data);
      setIsLoading(false);
      fetchIdentityVerification(data);
    } catch (error) {
      setIsLoading(false);
    }
  };

  /**
   * Per-signer IDV badges come from their own call, made only when some
   * signer's security option is identity verification. A failure just leaves the badges off —
   * it never blocks the page.
   */
  const fetchIdentityVerification = async (request?: SignRequest | null) => {
    if (!anySignerRequiresIdv(request?.signers)) {
      setIdentityVerification([]);
      return;
    }
    try {
      setIdentityVerification(await getRequestIdentityVerification(id));
    } catch {
      setIdentityVerification([]);
    }
  };

  const fetchRequestAudit = async () => {
    try {
      setIsAuditLoading(true);
      // A fresh trail invalidates any earlier integrity result.
      setVerification(undefined);
      const auditData = await getRequestAudit(id);
      setRequestAuditMap(auditData);
      handleFullAudit(auditData);
    } catch (error) {
    } finally {
      setIsAuditLoading(false);
    }
  };

  const handleVerifyAudit = async () => {
    try {
      setIsVerifying(true);
      setVerification(await verifyRequestAudit(id));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsVerifying(false);
    }
  };

  /** Every audit event, newest first — the timeline's default view. */
  const flattenAudits = (auditMap: Audits): RequestAudit[] =>
    Object.values(auditMap)
      .flatMap((map) => Object.values(map as { [key: string]: RequestAudit[] }))
      .flat()
      .sort((a, b) => b.createdOn.localeCompare(a.createdOn));

  const handleFullAudit = (auditMap: Audits = requestAuditMap) => {
    setShowFullAuditBtn(false);
    setRequestAudits(flattenAudits(auditMap));
  };

  const handleIndividualAudit = (email: string) => {
    const audits = requestAuditMap["Signer"]?.[email]
      ? [...requestAuditMap["Signer"][email]].sort((a, b) =>
          b.createdOn.localeCompare(a.createdOn),
        )
      : [];
    if (audits.length > 0) {
      setRequestAudits(audits);
    }
  };

  // Recipient timestamps and decline reasons always come from the complete
  // audit map, so drilling into one signer's audit does not blank them.
  const allAudits = useMemo(
    () => flattenAudits(requestAuditMap),
    [requestAuditMap],
  );
  const recipientTimestamps = useMemo(
    () => buildRecipientTimestamps(allAudits),
    [allAudits],
  );
  const declineReasons = useMemo(
    () => buildDeclineReasons(allAudits),
    [allAudits],
  );
  const completedOn = useMemo(() => getCompletedOn(allAudits), [allAudits]);

  const handleMenuClose = () => setAnchorEl(null);

  const setLoader = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const canDownloadDocuments = hasAccessTo(
    strings.resources.sign,
    strings.downloadPermission,
  );


  const hasApprovers =
    isTruthy(signRequest?.approval?.approvalNeeded) &&
    isTruthy(signRequest?.approval?.reviewers);

  const auditGateBlock = isAuditNotInPlan ? (
    <FeatureGateBlock
      variant="inline"
      status={strings.FEATURE_STATUS_NOT_IN_PLAN}
    />
  ) : isAuditAdminDisabled ? (
    <FeatureGateBlock
      variant="inline"
      status={strings.FEATURE_STATUS_ADMIN_DISABLED}
      disabledMessage={(auditState as any).disabledMessage}
    />
  ) : undefined;

  const participantSharedProps = {
    anchorEl,
    isMenuOpen: isParticipantMenuOpen,
    setIsMenuOpen: (event: React.MouseEvent<HTMLElement>) => {
      setIsParticipantMenuOpen(true);
      setAnchorEl(event.currentTarget);
    },
    handleMenuClose: () => {
      setIsParticipantMenuOpen(false);
      handleMenuClose();
    },
    handleIndividualAudit,
    selectedParticipant,
    setSelectedParticipant,
    setShowFullAuditBtn,
    setLoader,
    fetchData,
    timestamps: recipientTimestamps,
    declineReasons,
  };

  return (
    <CenteredAuthenticatedLayout>
      <CustomLoader isLoading={loading} />

      <Box sx={styles.page} aria-busy={isLoading || !signRequest}>
        {isLoading || !signRequest ? (
          <ViewSignRequestSkeleton isDesktop={isDesktop} />
        ) : (
          <>
            <ViewSignRequestHeaderSection
              fetchData={fetchData}
              setSaveTemplatePopup={setSaveTemplatePopup}
              signRequest={signRequest}
              isSigned={isSigned}
              setIsMenuOpen={(event: React.MouseEvent<HTMLElement>) => {
                setIsHeaderMenuOpen(true);
                setAnchorEl(event.currentTarget);
              }}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              handleMenuClose={() => {
                setIsHeaderMenuOpen(false);
                setAnchorEl(null);
              }}
              isMenuOpen={isHeaderMenuOpen}
              isLoading={isLoading}
              setLoading={setIsLoading}
            />

            <SignRequestBanner
              status={signRequest.status}
              isSigned={isSigned}
              completedOn={completedOn}
            />

            <Box sx={styles.columns}>
              <Box sx={styles.column}>
                <DocumentsCard
                  signRequest={signRequest}
                  canDownload={canDownloadDocuments && isSigned}
                  mergedOnly={showMergedDocumentOnly}
                  withSummary={isSigned}
                  onPreview={(index) => {
                    setDocumentsPreviewIndex(index);
                    // Once signed there is a signed copy for the AI to read, so the same action
                    // opens the review screen instead of the plain viewer.
                    if (isSigned) {
                      setPostSignReviewOpen(true);
                    } else {
                      setDocumentsPreviewOpen(true);
                    }
                  }}
                />

                <AttachmentsCard
                  requestId={signRequest.id!}
                  signerNameByEmail={Object.fromEntries(
                    (signRequest.signers ?? [])
                      .filter((signer: any) => signer?.email)
                      .map((signer: any) => [
                        String(signer.email).toLowerCase(),
                        signer.name || signer.email,
                      ]),
                  )}
                />

                <CollectedDataCard
                  requestId={signRequest.id!}
                  signerNameByEmail={Object.fromEntries(
                    (signRequest.signers ?? [])
                      .filter((signer: any) => signer?.email)
                      .map((signer: any) => [
                        String(signer.email).toLowerCase(),
                        signer.name || signer.email,
                      ]),
                  )}
                />

                {hasApprovers && (
                  <RequestParticipants
                    {...participantSharedProps}
                    status={signRequest.status!}
                    type={strings.requestParticipantsType.reviewers}
                    participants={signRequest.approval?.reviewers || []}
                    needOrder={isTruthy(signRequest.approval?.orderNeeded)}
                    reqId={signRequest.id!}
                    signRequest={signRequest}
                  />
                )}

                <RequestParticipants
                  {...participantSharedProps}
                  status={signRequest.status!}
                  type={strings.requestParticipantsType.signers}
                  participants={signRequest.signers || []}
                  needOrder={isTruthy(signRequest.signerOrderRequired)}
                  reqId={signRequest.id!}
                  signRequest={signRequest}
                  identityVerification={identityVerification}
                />

                <MessageCard message={signRequest.emailMessage} />
              </Box>

              <Box sx={styles.column}>
                <AuditTrailCard
                  requestAudits={requestAudits}
                  isLoading={isAuditLoading}
                  handlerFunction={handleFullAudit}
                  setSelectedParticipant={setSelectedParticipant}
                  showFullAuditBtn={showFullAuditBtn}
                  gateBlock={auditGateBlock}
                  canVerify={hasWorkspacePermission("audit.view")}
                  onVerify={handleVerifyAudit}
                  isVerifying={isVerifying}
                  verification={verification}
                />
                {/* Request details — hidden for now.
                <RequestDetailsCard
                  signRequest={signRequest}
                  folderName={signFolderName}
                /> */}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <AppModal
        open={documentsPreviewOpen}
        onClose={() => setDocumentsPreviewOpen(false)}
        title="Documents"
        tone="neutral"
        // A document viewer — the widest step the system offers.
        size="xl"
      >
        {signRequest ? (
          <ViewDocuments
            request={signRequest}
            initialDocIndex={documentsPreviewIndex}
          />
        ) : null}
      </AppModal>

      {signRequest && postSignReviewOpen ? (
        <PostSignReview
          open={postSignReviewOpen}
          onClose={() => setPostSignReviewOpen(false)}
          signRequest={signRequest}
          initialDocIndex={documentsPreviewIndex}
        />
      ) : null}

      <GetSaveTemplatePopups
        signRequest={signRequest!}
        folderTree={folderTree}
        newTemplate={false}
        errors={errors}
        dispatch={dispatch}
        saveTemplatePopup={saveTemplatePopup}
        setSaveTemplatePopup={setSaveTemplatePopup}
        showFolderTreePopup={showFolderTreePopup}
        setShowFolderTreePopup={setShowFolderTreePopup}
        destFolder={destFolder}
        setDestFolder={setDestFolder}
        selectedFolderData={selectedFolderData}
        setSelectedFolderData={setSelectedFolderData}
        setSignRequest={setSignRequest}
        isRequest={isRequest}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default ViewSignRequest;
